/**
 * Feature Flags Utility
 * 
 * Manages feature flags for gradual rollout and A/B testing
 * Supports percentage-based rollouts and user-specific overrides
 */

import { useState, useEffect } from 'react';

/**
 * Default feature flag configuration
 */
const DEFAULT_FLAGS = {
  optimizedPatientSummary: true, // Ultra-optimized plugin-based PatientSummary
  enableVirtualization: true, // Virtual scrolling for sections
  enableWebWorkers: true, // AI processing in web workers
  enableProgressiveLoading: true, // Progressive data loading
  enablePerformanceMonitoring: true, // Performance tracking
  enableSectionPlugins: true, // Plugin architecture
  showSummaryDebug: false, // Hide plugin debug panel by default (even in dev)
  enterprisePatientSummary: true, // Enterprise clinical command center redesign
};

/**
 * Feature flag manager class
 */
class FeatureFlagManager {
  constructor() {
    this.flags = { ...DEFAULT_FLAGS };
    this.overrides = {};
    this.rolloutPercentages = {};
    
    // Load from localStorage
    this.loadFromStorage();
    
    // Load from query params (for testing)
    this.loadFromQueryParams();
    
    // Check for server-side configuration
    this.loadFromServer();
  }

  /**
   * Load flags from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('featureFlags');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.overrides = parsed;
        this.flags = { ...this.flags, ...parsed };
      }
    } catch (error) {
      console.warn('[FeatureFlags] Failed to load from localStorage:', error);
    }
  }

  /**
   * Load flags from URL query parameters
   */
  loadFromQueryParams() {
    const params = new URLSearchParams(window.location.search);
    
    // Check for feature flag params (e.g., ?ff_usePatientSummaryV2=true)
    for (const [key, value] of params) {
      if (key.startsWith('ff_')) {
        const flagName = key.substring(3);
        if (flagName in this.flags) {
          this.flags[flagName] = value === 'true';
          this.overrides[flagName] = value === 'true';
        }
      }
    }
    
    // Check for rollout percentage override (e.g., ?ff_rollout_flagName=50)
    for (const [key, value] of params) {
      if (key.startsWith('ff_rollout_')) {
        const flagName = key.substring(11);
        const percentage = parseInt(value, 10);
        if (!isNaN(percentage) && flagName in this.flags) {
          this.rolloutPercentages[flagName] = percentage;
        }
      }
    }
  }

  /**
   * Load flags from server configuration
   */
  async loadFromServer() {
    try {
      // Skip server fetch in development or if endpoint doesn't exist
      if (process.env.NODE_ENV === 'development') {
        return;
      }
      
      // In production, this would fetch from an API endpoint
      const response = await fetch(require('../config/routes').default.api.featureFlags(), {
        credentials: 'include',
      });
      
      if (response.ok) {
        const serverFlags = await response.json();
        this.flags = { ...this.flags, ...serverFlags.flags };
        this.rolloutPercentages = { ...this.rolloutPercentages, ...serverFlags.rollouts };
      } else if (response.status === 404) {
        // Feature flags endpoint not implemented yet - use defaults
      }
    } catch (error) {
      // Network error or other issue - use defaults
    }
  }

  /**
   * Check if a feature is enabled
   */
  isEnabled(flagName) {
    // Check for explicit override first
    if (flagName in this.overrides) {
      return this.overrides[flagName];
    }
    
    // Check for percentage-based rollout
    if (flagName in this.rolloutPercentages) {
      const percentage = this.rolloutPercentages[flagName];
      if (percentage > 0 && percentage < 100) {
        // Use deterministic hash based on user ID for consistent experience
        const userId = this.getUserId();
        const hash = this.hashCode(userId + flagName);
        const bucket = Math.abs(hash) % 100;
        return bucket < percentage;
      }
    }
    
    // Return the flag value
    return this.flags[flagName] || false;
  }

  /**
   * Set a feature flag value (for testing)
   */
  setFlag(flagName, value) {
    if (flagName in this.flags) {
      this.flags[flagName] = value;
      this.overrides[flagName] = value;
      this.saveToStorage();
    }
  }

  /**
   * Set rollout percentage for a flag
   */
  setRolloutPercentage(flagName, percentage) {
    this.rolloutPercentages[flagName] = Math.max(0, Math.min(100, percentage));
  }

  /**
   * Reset all overrides
   */
  reset() {
    this.flags = { ...DEFAULT_FLAGS };
    this.overrides = {};
    localStorage.removeItem('featureFlags');
  }

  /**
   * Save current overrides to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem('featureFlags', JSON.stringify(this.overrides));
    } catch (error) {
      console.warn('[FeatureFlags] Failed to save to localStorage:', error);
    }
  }

  /**
   * Get user ID for consistent bucketing
   */
  getUserId() {
    // Try to get from various sources
    let userId = localStorage.getItem('userId');
    
    if (!userId) {
      // Try from JWT token
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.userId || payload.sub || payload.id;
        } catch (error) {
          // Invalid token
        }
      }
    }
    
    if (!userId) {
      // Generate and store a random ID
      userId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('userId', userId);
    }
    
    return userId;
  }

  /**
   * Simple hash function for deterministic bucketing
   */
  hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash;
  }

  /**
   * Export current state for debugging
   */
  exportState() {
    return {
      flags: this.flags,
      overrides: this.overrides,
      rolloutPercentages: this.rolloutPercentages,
      userId: this.getUserId(),
    };
  }
}

// Create singleton instance
const featureFlagManager = new FeatureFlagManager();

// Export convenience functions
export const isFeatureEnabled = (flagName) => featureFlagManager.isEnabled(flagName);
export const setFeatureFlag = (flagName, value) => featureFlagManager.setFlag(flagName, value);
export const setRolloutPercentage = (flagName, percentage) => featureFlagManager.setRolloutPercentage(flagName, percentage);
export const resetFeatureFlags = () => featureFlagManager.reset();
export const getFeatureFlagState = () => featureFlagManager.exportState();

// React hook for feature flags
export const useFeatureFlag = (flagName) => {
  const [enabled, setEnabled] = useState(() => featureFlagManager.isEnabled(flagName));
  
  useEffect(() => {
    // Re-check on mount in case flags were updated
    setEnabled(featureFlagManager.isEnabled(flagName));
    
    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === 'featureFlags') {
        featureFlagManager.loadFromStorage();
        setEnabled(featureFlagManager.isEnabled(flagName));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [flagName]);
  
  return enabled;
};

// Export the manager for advanced usage
export { featureFlagManager };
export default featureFlagManager;
